import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ShoppingCart,
  Package,
  ArrowULeftDown,
  ChartBar,
  Users,
  GearSix,
  Warning,
  Clock,
  CurrencyDollar,
  Receipt,
  PencilSimpleLine,
  DotsSixVertical,
  ArrowsClockwise,
  ChartLineUp
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUTTON_IMAGES = {
  sale: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/b35b8b6b3d017361cf28dd4cebf1352c2b17ee4e6a1bb8d006995750a12a2018.png',
  stock: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/d53f5d89dfc9b8378774fdb4a3631c618700cf1c1d19dbdd6b593de7609add73.png',
  return: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/2529b853d26bb15cf1d36f03a59002f8f8152d7479d9b54cc6ee3eef003d549a.png',
  reports: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/ae9b4ed544c074bdf59a8679b15a88dbaa1d0aefc34b8faec74ab0828558ff8e.png',
  customers: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/a4daf9869fa0e7375b27d40ff94445af90e4676cf04327759d45981aa1fa20d1.png',
  products: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/ec93d5482eabbcbc7eca4757b38ea031dd96db181f6271ae45cef33d08633378.png',
  settings: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/7d17df311db701aecbb3290fc23335c2c2a3f896bdec9d11038497547f8f83d2.png',
  reprint: 'https://static.prod-images.emergentagent.com/jobs/912750ef-fe82-4662-9a3e-72ea98f1362e/images/5a1368fc3817f0d0c50bf64381ac637d834e1589936ab9fe0ba3351987518add.png',
};

// Default button order
// Default rows: Row1 = sale/return (big), Row2 = customers/products/stock, Row3 = reports/settings
const DEFAULT_ROW1 = ['sale', 'return', 'reprint'];
const DEFAULT_ROW2 = ['customers', 'products', 'stock'];
const DEFAULT_ROW3 = ['dashboard', 'reports', 'settings'];
const DEFAULT_ORDER = [...DEFAULT_ROW1, ...DEFAULT_ROW2, ...DEFAULT_ROW3];

const BUTTON_CONFIG = {
  sale: { permission: 'sales', to: '/sales/new', icon: ShoppingCart, labelKey: 'newSale', descKey: 'createInvoice', testId: 'home-new-sale-btn', image: BUTTON_IMAGES.sale },
  return: { permission: 'returns', to: '/returns/new', icon: ArrowULeftDown, labelKey: 'returnLabel', descKey: 'processReturn', testId: 'home-return-btn', image: BUTTON_IMAGES.return },
  products: { permission: 'products', to: '/products', icon: PencilSimpleLine, labelKey: 'addChangeProduct', descKey: 'manageProductCatalog', testId: 'home-products-btn', image: BUTTON_IMAGES.products },
  stock: { permission: 'inventory', to: '/inventory/add', icon: Package, labelKey: 'addStock', descKey: 'receiveInventory', testId: 'home-add-stock-btn', image: BUTTON_IMAGES.stock },
  customers: { permission: 'customers', to: '/customers', icon: Users, labelKey: 'customers', descKey: 'manageCustomers', testId: 'home-customers-btn', image: BUTTON_IMAGES.customers },
  reports: { permission: 'reports', to: '/reports', icon: ChartBar, labelKey: 'reports', descKey: 'viewAnalytics', testId: 'home-reports-btn', image: BUTTON_IMAGES.reports },
  dashboard: { permission: 'reports', to: '/dashboard', icon: ChartLineUp, labelKey: 'dashboard', descKey: 'chartsGraphs', testId: 'home-dashboard-btn', image: BUTTON_IMAGES.reports },
  reprint: { permission: 'sales', to: '/reports?tab=sales-history', icon: Receipt, labelKey: 'reprintInvoice', descKey: 'viewPastInvoices', testId: 'home-reprint-btn', image: BUTTON_IMAGES.reprint },
  settings: { permission: 'settings', to: '/settings', icon: GearSix, labelKey: 'adminSettings', descKey: 'configureApp', testId: 'home-settings-btn', image: BUTTON_IMAGES.settings },
};

function SortableButton({ id, config, t, big, medium }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
  };

  const Icon = config.icon;

  return (
    <div ref={setNodeRef} style={style} className="relative group/card">
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 z-10 p-1.5 rounded-lg opacity-0 group-hover/card:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        style={{ background: 'var(--dm-bg-deep)', color: 'var(--dm-text-muted)' }}
        data-testid={`drag-${id}`}
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>

      <Link
        to={config.to}
        data-testid={config.testId}
        className={`
          flex items-center
          border rounded-2xl shadow-md overflow-hidden
          transition-all duration-300 ease-out
          hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]
          ${big ? 'h-[200px]' : medium ? 'h-[180px]' : 'h-[160px]'}
        `}
        style={{ background: 'var(--dm-bg-card)', borderColor: 'var(--dm-border)' }}
      >
        {/* Left Content */}
        <div className="flex-1 p-5 lg:p-6 flex flex-col justify-center">
          <div className={`${big ? 'w-14 h-14' : 'w-12 h-12'} mb-3 rounded-xl flex items-center justify-center group-hover/card:scale-110 transition-transform duration-300`}
            style={{ background: `var(--primary)`, color: '#FFFFFF' }}>
            <Icon size={big ? 32 : 26} weight="duotone" />
          </div>
          <h3 className={`${big ? 'text-xl lg:text-2xl' : 'text-lg lg:text-xl'} font-bold text-[var(--text)] leading-tight`} style={{ fontFamily: 'Outfit' }}>
            {t(config.labelKey)}
          </h3>
          <p className={`${big ? 'text-sm' : 'text-xs lg:text-sm'} font-bold mt-1`} style={{ color: 'var(--dm-text-muted)' }}>
            {t(config.descKey)}
          </p>
        </div>

        {/* Right Image */}
        {config.image && (
          <div className={`${big ? 'w-28 h-28 lg:w-36 lg:h-36' : 'w-20 h-20 lg:w-28 lg:h-28'} flex-shrink-0 mr-3 rounded-xl overflow-hidden max-w-[30%] bg-gray-900`}>
            <img src={config.image} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
      </Link>
    </div>
  );
}

function StatWidget({ icon: Icon, label, value }) {
  return (
    <div className="backdrop-blur-sm p-4 rounded-xl flex items-center gap-4"
      style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
      <div className="p-3 rounded-xl" style={{ background: 'var(--dm-accent-hover)', color: 'var(--dm-accent)' }}>
        <Icon size={24} weight="duotone" />
      </div>
      <div className="flex-1">
        <p className="text-sm" style={{ color: 'var(--dm-text-muted)' }}>{label}</p>
        <p className="text-2xl font-bold font-mono text-[var(--text)]">{value}</p>
      </div>
    </div>
  );
}

function ActivityItem({ type, description, time }) {
  const typeConfig = {
    sale: { icon: ShoppingCart, color: '#22C55E', bg: 'rgba(34,197,94,0.1)' },
    stock: { icon: Package, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
    return: { icon: ArrowULeftDown, color: '#F97316', bg: 'rgba(249,115,22,0.1)' },
  };
  const config = typeConfig[type] || typeConfig.stock;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-3 p-3 rounded-lg"
      style={{ background: 'var(--dm-bg-deep)', border: `1px solid var(--dm-border)` }}>
      <div className="p-2 rounded-lg" style={{ background: config.bg }}>
        <Icon size={18} style={{ color: config.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[var(--text)] text-sm truncate">{description}</p>
        <p className="text-xs" style={{ color: 'var(--dm-text-muted)' }}>{time}</p>
      </div>
    </div>
  );
}

export default function Home() {
  const { hasPermission } = useAuth();
  const { t } = useLanguage();
  const { currentTheme } = useTheme();
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);

  // Button order — load from localStorage or default
  const [buttonOrder, setButtonOrder] = useState(() => {
    try {
      const saved = localStorage.getItem('dm_button_order');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate: must contain all keys
        if (DEFAULT_ORDER.every(k => parsed.includes(k)) && parsed.length === DEFAULT_ORDER.length) {
          return parsed;
        }
      }
    } catch (e) { /* ignore */ }
    return DEFAULT_ORDER;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const visibleButtons = useMemo(() => {
    return buttonOrder.filter(id => {
      const config = BUTTON_CONFIG[id];
      return config && hasPermission(config.permission);
    });
  }, [buttonOrder, hasPermission]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = buttonOrder.indexOf(active.id);
    const newIndex = buttonOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = [...buttonOrder];
    newOrder.splice(oldIndex, 1);
    newOrder.splice(newIndex, 0, active.id);
    setButtonOrder(newOrder);
    localStorage.setItem('dm_button_order', JSON.stringify(newOrder));
  };

  const handleResetOrder = () => {
    setButtonOrder(DEFAULT_ORDER);
    localStorage.removeItem('dm_button_order');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, salesRes, lowRes] = await Promise.all([
          axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API_URL}/api/dashboard/recent-sales?limit=5`, { withCredentials: true }),
          axios.get(`${API_URL}/api/dashboard/low-stock?limit=5`, { withCredentials: true }),
        ]);
        setStats(statsRes.data);
        setRecentSales(salesRes.data);
        setLowStock(lowRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const activities = recentSales.map(sale => ({
    type: 'sale',
    description: `${sale.invoice_number} - $${sale.grand_total?.toFixed(2)}`,
    time: new Date(sale.created_at).toLocaleTimeString()
  }));

  const isCustomOrder = JSON.stringify(buttonOrder) !== JSON.stringify(DEFAULT_ORDER);

  return (
    <div className="space-y-8 animate-slide-up" data-testid="home-page">
      {/* Welcome Header */}
      <div className="text-center py-4">
        <h1 className="text-3xl lg:text-4xl font-bold text-[var(--text)] mb-2" style={{ fontFamily: 'Outfit' }}>
          Dukaan<span style={{ color: 'var(--dm-accent)' }}>Mate</span>
        </h1>
        <p style={{ color: 'var(--dm-text-muted)' }}>{t('appTagline')}</p>
      </div>

      {/* Reset order button (only visible when custom) */}
      {isCustomOrder && (
        <div className="flex justify-end">
          <button
            onClick={handleResetOrder}
            data-testid="reset-order-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:opacity-80"
            style={{ background: 'var(--dm-bg-card)', color: 'var(--dm-text-muted)', border: `1px solid var(--dm-border)` }}
          >
            <ArrowsClockwise size={14} />
            Reset layout
          </button>
        </div>
      )}

      {/* Main Action Buttons — Draggable */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={visibleButtons} strategy={rectSortingStrategy}>
          {!isCustomOrder ? (
            /* Default: 3 explicit rows for consistent sizing */
            <div className="space-y-4 lg:space-y-5">
              {/* Row 1: 3 items — New Sale, Return, Reprint Invoice */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
                {DEFAULT_ROW1.filter(id => visibleButtons.includes(id)).map((id) => (
                  <SortableButton key={id} id={id} config={BUTTON_CONFIG[id]} t={t} big />
                ))}
              </div>
              {/* Row 2: 3 items — Customers, Product, Stock */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
                {DEFAULT_ROW2.filter(id => visibleButtons.includes(id)).map((id) => (
                  <SortableButton key={id} id={id} config={BUTTON_CONFIG[id]} t={t} medium />
                ))}
              </div>
              {/* Row 3: 3 items — Dashboard, Reports, Admin Settings */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
                {DEFAULT_ROW3.filter(id => visibleButtons.includes(id)).map((id) => (
                  <SortableButton key={id} id={id} config={BUTTON_CONFIG[id]} t={t} />
                ))}
              </div>
            </div>
          ) : (
            /* Custom order: single grid */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {visibleButtons.map((id) => (
                <SortableButton key={id} id={id} config={BUTTON_CONFIG[id]} t={t} />
              ))}
            </div>
          )}
        </SortableContext>
      </DndContext>

      {/* Stats Widgets */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatWidget icon={CurrencyDollar} label={t('todaySales')} value={`$${stats?.today_sales?.toLocaleString() || 0}`} />
        <StatWidget icon={Receipt} label={t('todayInvoices')} value={stats?.today_invoices || 0} />
        <StatWidget icon={Warning} label={t('lowStockItems')} value={stats?.low_stock_items || 0} />
        <StatWidget icon={Package} label={t('totalProducts')} value={stats?.total_products || 0} />
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="rounded-xl p-4" style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
          <div className="flex items-center gap-2 mb-4">
            <Clock size={20} style={{ color: 'var(--dm-accent)' }} />
            <h3 className="font-semibold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('recentActivity')}</h3>
          </div>
          <div className="space-y-2">
            {activities.length === 0 ? (
              <p className="text-center py-4" style={{ color: 'var(--dm-text-muted)' }}>{t('noRecentActivity')}</p>
            ) : (
              activities.map((activity, i) => <ActivityItem key={i} {...activity} />)
            )}
          </div>
        </div>

        {/* Low Stock Alerts */}
        <div className="rounded-xl p-4" style={{ background: 'var(--dm-bg-card)', border: `1px solid var(--dm-border)` }}>
          <div className="flex items-center gap-2 mb-4">
            <Warning size={20} className="text-[#F97316]" />
            <h3 className="font-semibold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('lowStockAlerts')}</h3>
          </div>
          <div className="space-y-2">
            {lowStock.length === 0 ? (
              <p className="text-center py-4" style={{ color: 'var(--dm-text-muted)' }}>{t('allStockHealthy')}</p>
            ) : (
              lowStock.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-3 rounded-lg"
                  style={{ background: 'var(--dm-bg-deep)', border: `1px solid var(--dm-border)` }}>
                  <div>
                    <p className="text-[var(--text)] text-sm">{item.name}</p>
                    <p className="text-xs" style={{ color: 'var(--dm-text-muted)' }}>{item.product_code}</p>
                  </div>
                  <span className={`font-mono px-2 py-1 text-sm rounded ${
                    item.current_stock === 0 ? 'bg-[#EF4444]/20 text-[#EF4444]' : 'bg-[#F97316]/20 text-[#F97316]'
                  }`}>
                    {item.current_stock} {t('left')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
